/**
 * Maps a numeric Bank of Israel code (returned by the bank-confirmation OCR
 * processor as `bankCode`) to a localized bank display name. CourierConnect
 * does not have a Pipedrive options endpoint (unlike PartnerOS), so we ship a
 * static list of the major Israeli banks with their Bank of Israel codes.
 *
 * Monday's Bank Code column stores the numeric code only. One Zero's Monday
 * identifier is `וואן זירו 18`: the form shows the Hebrew name, and the sync
 * writes `18`.
 */

export interface IsraeliBank {
  code: string;
  name: string;
}

export const ISRAELI_BANKS: IsraeliBank[] = [
  { code: '9', name: 'בנק הדואר' },
  { code: '10', name: 'בנק לאומי' },
  { code: '11', name: 'בנק דיסקונט' },
  { code: '12', name: 'בנק הפועלים' },
  { code: '13', name: 'בנק איגוד' },
  { code: '14', name: 'בנק אוצר החייל' },
  { code: '17', name: 'בנק מרכנתיל דיסקונט' },
  { code: '18', name: 'וואן זירו' },
  { code: '20', name: 'בנק מזרחי טפחות' },
  { code: '26', name: 'יו בנק' },
  { code: '31', name: 'הבנק הבינלאומי' },
  { code: '46', name: 'בנק מסד' },
  { code: '52', name: 'בנק פועלי אגודת ישראל' },
  { code: '54', name: 'בנק ירושלים' },
  { code: '68', name: 'בנק דקסיה ישראל' },
];

/** OCR sometimes prints the bank name instead of code 18. */
const ONE_ZERO_ALIASES = ['וואן זירו', 'one zero', 'onezero'];

const compact = (value: string): string =>
  value.trim().toLowerCase().replace(/[\s\-_.]+/g, '');

/**
 * Digits only, with leading zeros removed so `018` matches code `18`.
 * Returns undefined when the value has no digits.
 */
const normalizeCode = (value: string | null | undefined): string | undefined => {
  if (!value) return undefined;
  const digits = String(value).replace(/\D/g, '').replace(/^0+/, '');
  return digits.length > 0 ? digits : undefined;
};

/**
 * Resolve a Bank of Israel code or a known bank-name alias to a bank.
 * Numeric codes win when both are present (for example `וואן זירו 18`).
 */
export const resolveIsraeliBank = (
  value: string | null | undefined,
): IsraeliBank | undefined => {
  if (!value || !String(value).trim()) return undefined;

  const code = normalizeCode(value);
  if (code) {
    const byCode = ISRAELI_BANKS.find((bank) => bank.code === code);
    if (byCode) return byCode;
  }

  const alias = compact(String(value));
  if (!alias) return undefined;
  return ISRAELI_BANKS.find((bank) => {
    if (compact(bank.name) === alias) return true;
    return (
      bank.code === '18' &&
      ONE_ZERO_ALIASES.some((candidate) => compact(candidate) === alias)
    );
  });
};

export const resolveBankNameFromCode = (
  bankCode: string | null | undefined,
): string | undefined => resolveIsraeliBank(bankCode)?.name;
